import { CheckOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";

export type LangCode = "en" | "ar";

type Props = {
  open: boolean;
  onClose: () => void;
  current: LangCode;
  onPick: (lang: LangCode) => void;
};

export default function LanguageSwitchModal({
  open,
  onClose,
  current,
  onPick,
}: Props) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const select = (lang: LangCode) => {
    if (lang === current) {
      onClose();
      return;
    }
    onPick(lang);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={400}
      rootClassName="ac-lang-modal"
      classNames={{
        mask: "ac-lang-modal__mask",
        content: "ac-lang-modal__shell",
        body: "ac-lang-modal__ant-body",
      }}
      styles={{
        content: { padding: 0 },
        body: { padding: 0 },
      }}
      destroyOnClose
      closable={false}
      title={null}
      zIndex={1100}
    >
      <div className="ac-lang-modal__wrap" dir={isRtl ? "rtl" : "ltr"}>
        <button
          type="button"
          className="ac-lang-modal__close"
          onClick={onClose}
          aria-label={t("services.close")}
        >
          <span aria-hidden>×</span>
        </button>
        <div className="ac-lang-modal__accent" aria-hidden />
        <div className="ac-lang-modal__head">
          <h2
            id="ac-lang-modal-title"
            className={`ac-lang-modal__title${isRtl ? " ac--font-expo" : ""}`}
          >
            {t("menu.languageModalTitle")}
          </h2>
          <p className={`ac-lang-modal__sub${isRtl ? " ac--font-expo" : ""}`}>
            {t("menu.languageModalHint")}
          </p>
        </div>
        <div
          className="ac-lang-modal__grid"
          role="listbox"
          aria-labelledby="ac-lang-modal-title"
        >
          <button
            type="button"
            role="option"
            aria-selected={current === "en"}
            className={`ac-lang-modal__opt${
              current === "en" ? " ac-lang-modal__opt--current" : ""
            }`}
            onClick={() => select("en")}
          >
            <span className="ac-lang-modal__flag">
              <img
                src="assets/imgs/flag-uk.png"
                alt=""
                width={40}
                height={30}
                loading="lazy"
              />
            </span>
            <span className="ac-lang-modal__text">
              <span className="ac-lang-modal__label">
                {t("menu.langEnglish")}
              </span>
              <span className="ac-lang-modal__native" lang="en">
                English
              </span>
            </span>
            {current === "en" ? (
              <CheckOutlined className="ac-lang-modal__check" aria-hidden />
            ) : (
              <span className="ac-lang-modal__check-placeholder" aria-hidden />
            )}
          </button>
          <button
            type="button"
            role="option"
            aria-selected={current === "ar"}
            className={`ac-lang-modal__opt${
              current === "ar" ? " ac-lang-modal__opt--current" : ""
            }`}
            onClick={() => select("ar")}
          >
            <span className="ac-lang-modal__flag">
              <img
                src="assets/imgs/flag-sa.png"
                alt=""
                width={40}
                height={30}
                loading="lazy"
              />
            </span>
            <span className="ac-lang-modal__text">
              <span className="ac-lang-modal__label">
                {t("menu.langArabic")}
              </span>
              <span className="ac-lang-modal__native" lang="ar">
                العربية
              </span>
            </span>
            {current === "ar" ? (
              <CheckOutlined className="ac-lang-modal__check" aria-hidden />
            ) : (
              <span className="ac-lang-modal__check-placeholder" aria-hidden />
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
